import * as FluxUtils from 'flux/utils';
import { t } from 'i18next';

import { ProjectInstance } from '../utils/ProjectInstance';
import { SortType } from './PersistentStore';
import { SortChainEntry, SortHelper } from '../utils/SortHelper';
import { PconLoginOrganization, PconLoginStore } from './PconLoginStore';
import { UIHelper } from '../utils/UIHelper';
import { DispatcherBaseAction, AppDispatcher } from '../dispatcher/AppDispatcher';
import { ActionConstants } from '../constants/ActionConstants';
import { PRODUCT_NAME } from '../App';

import utils = egr.wcf.utils;

interface TemplateItem {
    name: string;
    image: string;
    id: number;
}

interface ProjectManagerStoreState {
    projects: Array<ProjectInstance>;
    editMode: boolean;
    selectedProjects: Array<string>;
    loadedProject: ProjectInstance | null;
    templates: Array<TemplateItem>;
    filter: string | null;
}

class ProjectManagerStoreClass extends FluxUtils.ReduceStore<ProjectManagerStoreState, unknown> {
    public static mapName(value: ProjectInstance): string {
        return value.name == null ? '' : value.name;
    }

    public static mapDate(value: ProjectInstance): number {
        return value.mtime;
    }

    public getInitialState(): ProjectManagerStoreState {
        return {
            projects: [],
            editMode: false,
            selectedProjects: [],
            loadedProject: null,
            templates: [
                {name: 'Empty', image: './images/templates/Template_1.png', id: 0},
                {name: 'Photo', image: '', id: 1},
                {name: 'Classic', image: './images/templates/Template_2.png', id: 2},
                {name: 'Industrial', image: './images/templates/Template_3.png', id: 3},
            ],
            filter: null,
        };
    }

    public getCurrentProject(): ProjectInstance | null {
        return this.getState().loadedProject;
    }

    public currentProjectWasSaved(): boolean {
        const project: ProjectInstance | null = this.getCurrentProject();
        if (project != null) {
            return !project.isNew;
        }
        return false;
    }

    public getCurrentProjectName(defaultName: string): string {
        const project: ProjectInstance | null = this.getCurrentProject();
        if (project != null) {
            return utils.string.isNullOrEmpty(project.name) ? defaultName : project.name;
        }
        return defaultName;
    }

    public getProjectName(asFileName: boolean, fallback?: string): string {
        let result: string | undefined = fallback;
        if (utils.string.isNullOrEmpty(result)) {
            result = PRODUCT_NAME;
        }

        const projectName: string | null = this.getCurrentProjectName(t('Unnamed'));
        if (utils.string.isNotNullOrEmpty(projectName)) {
            result = projectName;
        }

        if (asFileName) {
            const organization: PconLoginOrganization | null = PconLoginStore.getState().organization;
            if (organization != null && !utils.string.isNullOrEmpty(organization.name)) {
                const date: string = UIHelper.toDateString(new Date());
                result = `${organization.name}_${date}_${result}`;
            }

            return result.replace(/[^a-z0-9]/gi, '_');
        }
        return result;
    }

    public reduce(state: ProjectManagerStoreState, action: DispatcherBaseAction): ProjectManagerStoreState {
        // Note: this function should be `pure` -> we have to return a copy
        // of the current state if some property was changed

        switch (action.actionType) {
            case ActionConstants.SET_PROJECT_SELECTION:
                const testArray: Array<string> = state.selectedProjects.slice(); // clone
                if ((action.value == null) || (testArray.indexOf(action.value) !== -1)) {
                    return jQuery.extend({}, state, {
                        selectedProjects: []
                    });
                }
                return jQuery.extend({}, state, {
                    selectedProjects: [action.value]
                });
            case ActionConstants.TOGGLE_PROJECT_SELECTION:
                if (action.value == null) {
                    return jQuery.extend({}, state, {
                        selectedProjects: []
                    });
                }
                let newArray: Array<string> = state.selectedProjects.slice(); // clone
                if (newArray.indexOf(action.value) !== -1) {
                    newArray = newArray.filter((item: string) => { return item !== action.value; });
                } else {
                    newArray.push(action.value);
                }
                return jQuery.extend({}, state, {
                    selectedProjects: newArray
                });
            case ActionConstants.SET_LOADED_PROJECT:
                return jQuery.extend({}, state, {
                    loadedProject: action.value
                });
            case ActionConstants.SET_PROJECT_FILTER:
                return jQuery.extend({}, state, {
                    filter: action.value
                });
            case ActionConstants.SET_PROJECTS_SORT:
                return jQuery.extend({}, state, {
                    selectedProjects: []
                });
            case ActionConstants.CHANGE_HAMBURGER:
                return jQuery.extend(
                    {},
                    state,
                    {
                        editMode: false,
                        selectedProjects: []
                    }
                );
            case ActionConstants.PROJECT_EDITMODE:
                return jQuery.extend(
                    {},
                    state,
                    {editMode: action.value},
                    action.value ? {} : {selectedProjects: []}
                );
            case ActionConstants.PROJECT_LIST_UPDATE:
                return jQuery.extend(
                    {},
                    state,
                    {projects: action.value},
                    (action.value && action.value.length) ? {} : {editMode: false, selectedProjects: []}
                );
            case ActionConstants.RESET_PROJECT_RELATED_SETTINGS:
                return jQuery.extend({}, state, {
                    loadedProject: null
                });
            default:
                break;
        }
        return state;
    }

    public getSelectedProject(index: number): ProjectInstance | null {
        const state: ProjectManagerStoreState = this.getState();
        const selectedProjects: Array<string> = state.selectedProjects.slice();
        const projectId: string = selectedProjects[index];
        let result: ProjectInstance | null = null;
        if (projectId != null) {
            state.projects.map((project: ProjectInstance) => {
                if (project.id === projectId) {
                    result = project;
                }
            });
        }
        return result;
    }

    public getSortedProjects(sortType: SortType): Array<ProjectInstance> {
        const state: ProjectManagerStoreState = this.getState();
        return state.projects.sort(
            SortHelper.getSortFunctionForChain(this.getSortChain(sortType))
        );
    }

    private getSortChain(sortType: SortType): Array<SortChainEntry> {
        switch (sortType) {
            case 'name-up':
                return [
                    {
                        map: ProjectManagerStoreClass.mapName,
                        sort: SortHelper.sortCaseInsensitive
                    },
                    {
                        map: ProjectManagerStoreClass.mapDate,
                        sort: SortHelper.revertSortFunction(SortHelper.sortNumber)
                    }
                ];
            case 'name-down':
                return [
                    {
                        map: ProjectManagerStoreClass.mapName,
                        sort: SortHelper.revertSortFunction(SortHelper.sortCaseInsensitive)
                    },
                    {
                        map: ProjectManagerStoreClass.mapDate,
                        sort: SortHelper.revertSortFunction(SortHelper.sortNumber)
                    }
                ];
            case 'date-up':
                return [
                    {
                        map: ProjectManagerStoreClass.mapDate,
                        sort: SortHelper.sortNumber
                    },
                    {
                        map: ProjectManagerStoreClass.mapName,
                        sort: SortHelper.sortCaseInsensitive
                    }
                ];
            case 'date-down':
                return [
                    {
                        map: ProjectManagerStoreClass.mapDate,
                        sort: SortHelper.revertSortFunction(SortHelper.sortNumber)
                    },
                    {
                        map: ProjectManagerStoreClass.mapName,
                        sort: SortHelper.sortCaseInsensitive
                    }
                ];
            default:
                return [];
        }
    }
}

export let ProjectManagerStore: ProjectManagerStoreClass = new ProjectManagerStoreClass(AppDispatcher);


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/stores/ProjectManagerStore.ts